import { useEffect, useRef, useState } from "react";
import { FileText, Paperclip, Send, X } from "lucide-react";

import { cn } from "@/lib/utils";

import { ContentBlock } from "../api/messages";

export interface AttachedFile {
  id: string;
  file: File;
  preview?: string; // For images
  contentBlock: ContentBlock;
}

interface ChatInputProps {
  onSend: (message: string, attachments?: ContentBlock[]) => void;
  disabled?: boolean;
  placeholder?: string;
  initialValue?: string;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
}

const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
const MAX_TOTAL_SIZE = 30 * 1024 * 1024; // 30MB total for all files
const MAX_FILE_COUNT = 3; // Maximum 3 files per message

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data:mime;base64, prefix
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
  });
};

const isImageType = (mimeType: string) => mimeType.startsWith("image/");

export const ChatInput = ({
  onSend,
  disabled = false,
  placeholder = "What can I help you with?",
  initialValue = "",
  inputRef,
}: ChatInputProps) => {
  const [message, setMessage] = useState(initialValue);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = inputRef || internalTextareaRef;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update message when initialValue changes
  useEffect(() => {
    if (initialValue) {
      setMessage(initialValue);
    }
  }, [initialValue]);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [message]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      attachedFiles.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
    };
  }, [attachedFiles]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Check if adding these files would exceed the file count limit
    if (attachedFiles.length + files.length > MAX_FILE_COUNT) {
      alert(
        `You can only attach up to ${MAX_FILE_COUNT} files per message. Please remove some files first.`,
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setIsProcessingFiles(true);

    try {
      const newAttachments: AttachedFile[] = [];
      const skippedFiles: string[] = [];

      // Calculate current total size
      const currentTotalSize = attachedFiles.reduce(
        (sum, file) => sum + file.file.size,
        0,
      );

      for (const file of Array.from(files)) {
        // Validate file type
        if (!Object.keys(ACCEPTED_FILE_TYPES).includes(file.type)) {
          skippedFiles.push(`${file.name} (unsupported type)`);
          continue;
        }

        // Validate individual file size
        if (file.size > MAX_FILE_SIZE) {
          skippedFiles.push(
            `${file.name} (exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit)`,
          );
          continue;
        }

        // Calculate potential total size if we add this file
        const potentialTotalSize =
          currentTotalSize +
          newAttachments.reduce((sum, att) => sum + att.file.size, 0) +
          file.size;

        // Validate total size
        if (potentialTotalSize > MAX_TOTAL_SIZE) {
          skippedFiles.push(
            `${file.name} (would exceed ${MAX_TOTAL_SIZE / 1024 / 1024}MB total limit)`,
          );
          continue;
        }

        const base64Data = await fileToBase64(file);
        const isImage = isImageType(file.type);

        const contentBlock: ContentBlock = isImage
          ? {
              type: "image",
              data: base64Data,
              mimeType: file.type,
            }
          : {
              type: "file",
              data: base64Data,
              mimeType: file.type,
              filename: file.name,
            };

        newAttachments.push({
          id: `${Date.now()}-${file.name}`,
          file,
          preview: isImage ? URL.createObjectURL(file) : undefined,
          contentBlock,
        });
      }

      // Show warning if any files were skipped
      if (skippedFiles.length > 0) {
        alert(
          `The following files were skipped:\n\n${skippedFiles.join("\n")}`,
        );
      }

      setAttachedFiles((prev) => [...prev, ...newAttachments]);
    } finally {
      setIsProcessingFiles(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeAttachment = (id: string) => {
    setAttachedFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isProcessingFiles) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the entire component
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || isProcessingFiles) return;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Convert FileList to File[] and trigger the same validation logic
    const fileArray = Array.from(files);
    
    // Create a synthetic event to reuse handleFileSelect logic
    const syntheticEvent = {
      target: { files },
    } as React.ChangeEvent<HTMLInputElement>;

    await handleFileSelect(syntheticEvent);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const hasContent = message.trim() || attachedFiles.length > 0;
    if (hasContent && !disabled && !isProcessingFiles) {
      const contentBlocks =
        attachedFiles.length > 0
          ? attachedFiles.map((f) => f.contentBlock)
          : undefined;
      onSend(message.trim(), contentBlocks);
      setMessage("");
      // Cleanup previews
      attachedFiles.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      setAttachedFiles([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const hasContent = message.trim() || attachedFiles.length > 0;

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div 
        className={cn(
          "border rounded-lg flex flex-col transition-colors relative",
          isDragging 
            ? "border-sds-purple-60 border-2 bg-sds-purple-05" 
            : "border-sds-neutral-20 focus-within:border-sds-neutral-40"
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-sds-purple-05 border-2 border-dashed border-sds-purple-60 rounded-lg flex items-center justify-center z-10 pointer-events-none">
            <div className="text-center px-4">
              <p className="text-sm font-medium text-sds-purple-80">
                Drop files here
              </p>
              <p className="text-xs text-sds-neutral-60 mt-1">
                PDF, PNG, JPG, GIF, WebP (max {MAX_FILE_COUNT} files, {MAX_TOTAL_SIZE / 1024 / 1024}MB total)
              </p>
            </div>
          </div>
        )}
        {/* Attachments preview */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 px-3 pt-3">
            {attachedFiles.map((attachment) => (
              <div
                key={attachment.id}
                className="relative group flex items-center gap-2 bg-sds-neutral-05 rounded-md px-2 py-1.5 border border-sds-neutral-20"
              >
                {attachment.preview ? (
                  <img
                    src={attachment.preview}
                    alt={attachment.file.name}
                    className="w-8 h-8 object-cover rounded"
                  />
                ) : (
                  <FileText className="w-5 h-5 text-sds-neutral-60" />
                )}
                <span className="text-xs text-sds-neutral-80 max-w-[120px] truncate">
                  {attachment.file.name}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(attachment.id)}
                  className="p-0.5 rounded-full hover:bg-sds-neutral-20 transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-sds-neutral-60" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="flex items-end justify-between px-3 py-2 min-h-[46px]">
          {/* Attachment button */}
          <button
            type="button"
            onClick={openFilePicker}
            disabled={disabled || isProcessingFiles}
            className={cn(
              "p-1.5 rounded-md transition-colors flex-shrink-0 mr-2",
              disabled || isProcessingFiles
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-sds-neutral-10 text-sds-neutral-60 hover:text-sds-neutral-80"
            )}
            title="Attach files (PDF, images)"
          >
            <Paperclip className="w-[18px] h-[18px]" />
          </button>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={Object.entries(ACCEPTED_FILE_TYPES)
              .flatMap(([mime, exts]) => [mime, ...exts])
              .join(",")}
            onChange={handleFileSelect}
            className="hidden"
          />

          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            rows={1}
            className="flex-1 bg-transparent outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 text-sds-neutral-80 text-[14px] font-['Plus_Jakarta_Sans'] tracking-normal placeholder:text-sds-neutral-80 resize-none overflow-hidden py-1"
            style={{ boxShadow: "none", maxHeight: "200px" }}
          />
          <button
            type="submit"
            disabled={disabled || !hasContent || isProcessingFiles}
            className={cn(
              "border border-sds-neutral-20 rounded-md p-2 w-8 h-8 flex items-center justify-center transition-colors flex-shrink-0",
              disabled || !hasContent || isProcessingFiles
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-sds-neutral-00"
            )}
          >
            <Send className="w-[18px] h-[18px] text-sds-neutral-80" />
          </button>
        </div>
      </div>
    </form>
  );
};
